import { useState } from 'react';
import { useSelector } from '@legendapp/state/react';
import { Hash, ChevronRight, LogOut } from 'lucide-react';
import { sharedWithMe$, leaveSharedTag, refreshSharedTagsCache } from '@/stores/sharedTags$';
import { ui$, navigateOnMobile } from '@/stores/ui$';
import { cn } from '@/lib/utils';

export default function SharedWithMe() {
  const sharedWithMe = useSelector(() => sharedWithMe$.get());
  const selectedSharedTag = useSelector(() => ui$.selectedSharedTag.get());

  if (!sharedWithMe || sharedWithMe.length === 0) return null;

  // Group shared tags by owner
  const byOwner = {};
  for (const tag of sharedWithMe) {
    const key = tag.owner_id;
    if (!byOwner[key]) {
      byOwner[key] = {
        owner_id: tag.owner_id,
        owner_username: tag.owner_username,
        owner_avatar_url: tag.owner_avatar_url,
        tags: [],
      };
    }
    byOwner[key].tags.push(tag);
  }

  const handleSelectSharedTag = (tag) => {
    const current = selectedSharedTag;
    if (current?.id === tag.id) {
      // Deselect
      ui$.selectedSharedTag.set(null);
    } else {
      // Select shared tag and clear personal tag filter
      ui$.selectedTagFilter.set(null);
      ui$.selectedSharedTag.set({
        id: tag.id,
        tagPath: tag.tag_path,
        ownerName: tag.owner_username,
        ownerId: tag.owner_id,
      });
      navigateOnMobile();
    }
  };

  const handleLeave = async (e, tag) => {
    e.stopPropagation();
    if (!confirm(`Leave shared #${tag.tag_path}? You will no longer see notes from this shared tag.`)) return;
    await leaveSharedTag(tag.id);
    await refreshSharedTagsCache();
    if (selectedSharedTag?.id === tag.id) {
      ui$.selectedSharedTag.set(null);
    }
  };

  return (
    <div className="mt-6">
      {/* Header */}
      <div className="px-2 py-1.5 mb-1">
        <h3 className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider">
          Shared with me
        </h3>
      </div>

      <div className="space-y-0.5">
        {Object.values(byOwner).map((group) => (
          <OwnerGroup
            key={group.owner_id}
            group={group}
            selectedSharedTag={selectedSharedTag}
            onSelect={handleSelectSharedTag}
            onLeave={handleLeave}
          />
        ))}
      </div>
    </div>
  );
}

function OwnerGroup({ group, selectedSharedTag, onSelect, onLeave }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const initials = (group.owner_username || 'U')[0].toUpperCase();

  return (
    <div>
      {/* Owner header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <ChevronRight
          size={10}
          className={cn(
            "transition-transform flex-shrink-0",
            isExpanded && "rotate-90"
          )}
        />
        {group.owner_avatar_url ? (
          <img
            src={group.owner_avatar_url}
            alt={group.owner_username}
            className="w-4 h-4 rounded-full flex-shrink-0"
          />
        ) : (
          <div className="w-4 h-4 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[8px] font-medium flex-shrink-0">
            {initials}
          </div>
        )}
        <span className="truncate">{group.owner_username}</span>
      </button>

      {/* Shared tags under this owner */}
      {isExpanded && (
        <div className="mt-0.5">
          {group.tags.map((tag) => {
            const isSelected = selectedSharedTag?.id === tag.id;
            return (
              <div
                key={tag.id}
                onClick={() => onSelect(tag)}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm font-medium transition-colors group cursor-pointer",
                  isSelected
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
                style={{ paddingLeft: '32px' }}
              >
                <Hash size={10} className="flex-shrink-0 ml-0.5" />
                <span className="flex-1 truncate text-left">{tag.tag_path}</span>
                <button
                  onClick={(e) => onLeave(e, tag)}
                  className="p-0.5 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  title="Leave shared tag"
                >
                  <LogOut size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
